import {
	TableRow,
	TableColumn,
	TableHeader,
	TableBody,
	DataTable,
} from 'react-md'
import { TableContainer } from 'ui/StyledComponents/Tables'
import { voucherDialogTableHeaders } from './constants'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import type { CreateFormInputs } from './validation'
import { useFormContext } from 'react-hook-form'
import type { Vat, VoucherRow, VoucherRowHeader } from './types'
import useChartOfAccountByIds from './useChartOfAccountsByIds'
import { useSelectedFinanceCompany } from 'ui/ManagerFinance/hooks/useSelectedFinanceCompany'
import { useMemo } from 'react'

const Content = styled.div`
	overflow: auto;
`

const StyledDataTable = styled(DataTable)`
	height: auto;
	max-height: calc(100vh - 400px);
	td {
		white-space: nowrap;
	}
`

type VoucherFormTableProps = {
	vats?: Vat[]
}

const VoucherFormTable = (props: VoucherFormTableProps) => {
	const { vats } = props

	const intl = useIntl()

	const { watch } = useFormContext<CreateFormInputs>()

	const emptyArray = useMemo(() => [], [])

	const voucherRows = watch('voucherRows') ?? emptyArray

	const accountIds = useMemo(
		() => voucherRows.map((r) => r.account),
		[voucherRows],
	)

	const { company } = useSelectedFinanceCompany()

	const { chartOfAccounts } = useChartOfAccountByIds({
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
		accountIds,
	})

	const renderColumnValue = (row: VoucherRow, input: VoucherRowHeader) => {
		const value = row[input.key]
		if (input.key === 'vat') {
			const vat = vats?.find((v) => v.id === value)
			return vat ? `${vat.percent}%` : ''
		} else if (input.key === 'account') {
			const account = chartOfAccounts?.find((a) => a?.id === value)
			return account ? account.code : ''
		} else if (input.key === 'description') {
			const account = chartOfAccounts?.find((a) => a?.id === row['account'])
			return account ? account.name : ''
		} else {
			return value
		}
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{voucherDialogTableHeaders
					.filter((h) => h.show)
					.map((header) => (
						<TableColumn key={header.key}>
							<p className="text-strong text-subtle">{header.title(intl)}</p>
						</TableColumn>
					))}
			</TableRow>
		)
	}

	const renderTable = () => {
		return (
			<TableContainer>
				<StyledDataTable plain>
					<TableHeader
						style={{
							position: 'sticky',
							top: 0,
							zIndex: 1,
						}}
					>
						{renderTableHeaders()}
					</TableHeader>

					<TableBody>
						<>
							{voucherRows.map((row, rowIndex) => (
								<TableRow key={`${row.id}-${rowIndex}`}>
									{voucherDialogTableHeaders.map((input, index) => (
										<TableColumn key={`${input.key}-${index}`}>
											{renderColumnValue(row, input)}
										</TableColumn>
									))}
								</TableRow>
							))}
							<TableRow key="add-row">
								{/* TODO: ADDING ROW */}
								<TableColumn>
									<p>+</p>
								</TableColumn>
							</TableRow>
						</>
					</TableBody>
				</StyledDataTable>
			</TableContainer>
		)
	}

	return <Content>{renderTable()}</Content>
}

export default VoucherFormTable
